// src/views/AnalysisView/AnalysisTableColumns.jsx
import React, { useEffect, useRef, useState } from 'react'
import { Box, Typography, IconButton, Tooltip, Skeleton, TextField, CircularProgress } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff'
import SampleDetailsIcon from '@material-ui/icons/AssignmentIndOutlined'
import {
  FileDownloadOutlined as ReportIcon,
  Info as InfoIcon,
  Psychology as AIIcon,
  Edit as EditIcon,
} from '@material-ui/icons'
import { Link as RouterLink } from 'react-router-dom'
import AnalysisStatus from './AnalysisStatus'
import AnalysisPopover from '../FileUploadView/AnalysisPopover'
import GoToSampleDashboard from '../FileUploadView/components/GoToSampleDashboard'
import GoToCnvDashboard from '../FileUploadView/components/GoToCnvDashboard'
import GoToMethylationDashboard from '../FileUploadView/components/GoToMethylationDashboard'
import GoToUnifiedDashboard from '../FileUploadView/components/GoToUnifiedDashboard'
import GoToSomaticVariants from '../FileUploadView/components/GoToSomaticVariants'
import GoToSomaticStructural, { showSomaticStructuralButton } from '../FileUploadView/components/GoToSomaticStructural'
import GoToRnaAnalysis from '../FileUploadView/components/GoToRnaAnalysis'
import GoToCohortDashboard from '../FileUploadView/components/GoToCohortDashboard'
import ResultButton, { ResultIconButton } from '../FileUploadView/components/ResultButton'
import TonalChip, { getToneColors } from '../FileUploadView/components/TonalChip'
import AnalysisSummaryPopup from './AnalysisSummaryPopup'
import CarrierAnalysisPopover from './CarrierAnalysisPopover'
import RunAIModal from './RunAIModal'
import SampleDetailsDialog from './sampleTools/SampleDetailsDialog'
import FileNameText from 'src/components/FileNameText'
import GeneCoverageDialog from './sampleTools/GeneCoverageDialog'
import IgvGeneBrowserDialog from './sampleTools/IgvGeneBrowserDialog'
import { canRunAI } from './lib/somaticAnalysis'
import { showUnifiedButton } from './lib/unifiedButton'
import { pairedDnaIdsOf, pairedRnaOf } from './lib/pairedRna'
import { checkReport, downloadReport } from 'src/api/variant'
import { updateSampleAlias } from 'src/api/analysis/list'
import { Icon } from '@iconify/react'
import activityFill from '@iconify/icons-eva/activity-fill'
import { PATH_DASHBOARD } from 'src/routes/paths'
import { batchPath, getRowBatch } from '../Batches/useBatches'

// Helper to format dates consistently
const formatDateTime = (isoString) => {
  if (!isoString) return 'N/A'
  return new Date(isoString).toLocaleString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// Helper to get analysis type details for chips/badges
const getAnalysisTypeDetails = (analysisData) => {
  const annotation = analysisData?.analysis_parameters?.annotation
  const fileType = analysisData?.analysis_type || 'Unknown'

  if (!annotation) {
    return {
      primary: { label: fileType, color: 'default' },
      secondary: null,
      hasAI: false,
    }
  }

  const analysisType = annotation.analysisType
  const sequencingType = annotation.type
  const targetedType = annotation.targetedType
  const hasAI = annotation.runAI || false

  // Primary chip - analysis type
  let primaryChip = { label: fileType, color: 'default' }

  if (analysisType) {
    switch (analysisType) {
      case 'Germline':
        primaryChip = { label: 'Germline', color: 'primary' }
        break
      case 'TumorOnly':
        primaryChip = { label: 'Tumor-Only', color: 'secondary' }
        break
      case 'TumorNormal':
        primaryChip = { label: 'Tumor/Normal', color: 'secondary' }
        break
      default:
        primaryChip = { label: analysisType, color: 'default' }
    }
  }

  // Secondary chip - sequencing details
  let secondaryChip = null
  if (sequencingType) {
    if (sequencingType === 'WholeGenome') {
      secondaryChip = { label: 'WGS', color: 'success' }
    } else if (sequencingType === 'Targeted' && targetedType) {
      const label =
        targetedType === 'Clinical Exome' ? 'Clinical Exome' : targetedType === 'Whole Exome' ? 'WES' : targetedType
      secondaryChip = { label, color: 'info' }
    }
  }

  return {
    primary: primaryChip,
    secondary: secondaryChip,
    hasAI,
  }
}

// Germline results read in the brand olive. Somatic tags keep a neutral chip
// with violet text, so they do not echo the filled violet result buttons;
// every other tag stays neutral.
const SOMATIC_TAGS = ['Somatic', 'Tumor-Only', 'Tumor/Normal', 'Tumour-only', 'Tumour/Normal']
const getTagTone = (tag) => {
  if (tag === 'Germline') return 'primary'
  if (SOMATIC_TAGS.includes(tag)) return 'somatic'
  return 'neutral'
}

// The somatic run mode as a second chip beside "Somatic", from the list row's
// `somatic_mode` ('tumour_only' | 'paired'; null on germline). Nothing renders
// when the field is absent, and an older row that already carries a
// Tumor-Only or Tumor/Normal tag is not given a second one.
export const SOMATIC_MODE_LABELS = { tumour_only: 'Tumour-only', paired: 'Tumour/Normal' }

export const typeTags = (analysisData) => {
  const tags = (analysisData.tags || []).filter((tag) => tag !== 'AI')
  const modeLabel = SOMATIC_MODE_LABELS[analysisData.somatic_mode]
  const hasModeTag = tags.some((t) => t === 'Tumor-Only' || t === 'Tumor/Normal')
  if (!modeLabel || hasModeTag) return tags
  const at = tags.indexOf('Somatic')
  return at === -1 ? [...tags, modeLabel] : [...tags.slice(0, at + 1), modeLabel, ...tags.slice(at + 1)]
}

// Chips, the AI mark, the details button, then Run AI when AI has not run, on
// one line. The minimum keeps a typical row (Germline, WES, details and Run
// AI: 241px) whole even when the table is at its tightest; longer sets take the
// room the table has left and wrap only when it runs out.
const typeCellSx = { display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', minWidth: 242 }

// The "DNA + RNA" cue (its own blue, so paired patients stand out): a DNA row with a paired RNA, or an RNA row listed on
// its own because its DNA is not on this page.
const sampleNames = (rnas) => rnas.map((rna) => rna.display_sample_name).filter(Boolean)
const pairedCueTitle = (row) => {
  const rnas = pairedRnaOf(row)
  if (rnas.length > 0) {
    const names = sampleNames(rnas)
    return names.length > 0 ? `Paired with RNA sample ${names.join(', ')}` : 'Paired with an RNA sample'
  }
  if (pairedDnaIdsOf(row).length > 0) return 'Paired with a DNA analysis; its results also open from the DNA row'
  return null
}

// Component for rendering analysis type chips
const AnalysisTypeChips = ({ analysisData, showRunAI, onRefresh }) => {
  const theme = useTheme()
  const [summaryOpen, setSummaryOpen] = useState(false)

  const tags = typeTags(analysisData)
  const hasAI = analysisData.tags.includes('AI')
  const pairedCue = pairedCueTitle(analysisData)

  return (
    <Box sx={typeCellSx}>
      {pairedCue && (
        <Tooltip title={pairedCue} arrow>
          <span>
            <TonalChip tone="info" label="DNA + RNA" data-testid="paired-cue" style={{ fontWeight: 700 }} />
          </span>
        </Tooltip>
      )}
      {tags.map((tag) => (
        <TonalChip
          key={tag}
          tone={getTagTone(tag) === 'somatic' ? 'neutral' : getTagTone(tag)}
          label={tag}
          style={getTagTone(tag) === 'somatic' ? { color: getToneColors(theme, 'somatic').text } : undefined}
          data-tone={getTagTone(tag)}
        />
      ))}
      {hasAI && (
        <Tooltip title="AI analysis included" arrow>
          <AIIcon style={{ fontSize: 18, color: theme.palette.secondary.main }} />
        </Tooltip>
      )}
      <Tooltip title="Show analysis details" arrow>
        <ResultIconButton onClick={() => setSummaryOpen(true)} aria-label="Show analysis details">
          <InfoIcon />
        </ResultIconButton>
      </Tooltip>
      {showRunAI && <RunAIButton analysisRow={analysisData} onRefresh={onRefresh} />}
      <AnalysisSummaryPopup open={summaryOpen} onClose={() => setSummaryOpen(false)} analysisData={analysisData} />
    </Box>
  )
}

// Component to show report availability as a document icon
const ReportAvailabilityIcon = ({ fileId, analysisType, isDone, sampleName }) => {
  const [hasReport, setHasReport] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)

  useEffect(() => {
    let isCancelled = false
    if (!isDone || !fileId) return

    const typeParam = analysisType === 'FASTQ' ? 'fastq' : 'vcf'
    checkReport(fileId, typeParam)
      .then((response) => {
        if (!isCancelled) setHasReport(response?.status === 200)
      })
      .catch(() => {
        if (!isCancelled) setHasReport(false)
      })

    return () => {
      isCancelled = true
    }
  }, [fileId, analysisType, isDone])

  if (!isDone || !hasReport) return null

  return (
    <Tooltip title={isDownloading ? 'Downloading…' : 'Download report (.docx)'} arrow>
      <ResultButton
        startIcon={isDownloading ? <CircularProgress size={14} color="inherit" /> : <ReportIcon />}
        onClick={async (e) => {
          e.stopPropagation()
          try {
            setIsDownloading(true)
            const response = await downloadReport(fileId)

            // Force filename to sample_name.docx
            const safeSampleName = (sampleName || 'analysis_report').toString().replace(/[^A-Za-z0-9._-]/g, '_')
            const filename = `${safeSampleName}_report.docx`

            const blobUrl = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = blobUrl
            link.download = filename
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(blobUrl)
          } catch (err) {
            // noop, optionally could show a snackbar
            console.error('Failed to download report', err)
          } finally {
            setIsDownloading(false)
          }
        }}
      >
        Report
      </ResultButton>
    </Tooltip>
  )
}

// Button to trigger AI analysis on a completed pipeline that didn't run AI.
// It sits after the Analysis Type chips, where the AI mark shows once AI has
// run. Compact and fixed in size (26px, px type, no breakpoints), so it looks
// the same on every screen, in both modes and at any browser font size.
const RunAIButton = ({ analysisRow, onRefresh }) => {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <>
      <Tooltip title="Run AI analysis on this result" arrow>
        <ResultButton
          variant="outlined"
          compact
          onClick={(e) => {
            e.stopPropagation()
            setModalOpen(true)
          }}
          startIcon={<AIIcon />}
        >
          Run AI
        </ResultButton>
      </Tooltip>
      <RunAIModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        analysisData={analysisRow}
        onSuccess={onRefresh}
      />
    </>
  )
}

// Sample names wrap between their parts (after _ . -). The minimum keeps a
// typical ID (up to about 13 characters) whole instead of breaking it
// mid-token when the table is tight.
const sampleNameSx = { flex: '0 1 auto', minWidth: 90, maxWidth: 200 }

const sampleDisplayLabel = (analysisRow) => {
  if (analysisRow.sample_alias) {
    return analysisRow.display_sample_name || analysisRow.sample_alias
  }
  if (analysisRow.analysis_type === 'GENESUS-COHORT' || analysisRow.analysis_type === 'EXOMEDEPTH-COHORT') {
    return analysisRow.analysis_parameters?.cohort_name || 'Unknown Cohort'
  }
  return analysisRow.display_sample_name || analysisRow.sample_name
}

// Analyses of one sample file. Sequence files also carry QC and coverage.
const SEQUENCE_ANALYSIS_TYPES = ['FASTQ', 'ONT_BAM', 'PACBIO_BAM']
const SAMPLE_ANALYSIS_TYPES = ['VCF', ...SEQUENCE_ANALYSIS_TYPES]

// Open state for a per-row dialog. The table keys rows by position, so a refetch
// that shifts the list hands this cell a different row; the dialog keeps the
// row it was opened for.
const useRowDialog = (analysisRow) => {
  const [target, setTarget] = useState(null)
  const [open, setOpen] = useState(false)

  const openDialog = (e) => {
    e.stopPropagation()
    setTarget({
      fileId: analysisRow.file_id,
      analysisId: analysisRow.analysis_id,
      sampleName: analysisRow.sample_name,
      sampleLabel: sampleDisplayLabel(analysisRow),
    })
    setOpen(true)
  }

  return { target, open, openDialog, closeDialog: () => setOpen(false) }
}

// Gene coverage for sequenced samples, in a dialog.
const CoverageButton = ({ analysisRow }) => {
  const { target, open, openDialog, closeDialog } = useRowDialog(analysisRow)

  return (
    <>
      <Tooltip title="Gene coverage" arrow>
        <ResultButton onClick={openDialog}>Coverage</ResultButton>
      </Tooltip>
      {target && (
        <GeneCoverageDialog open={open} onClose={closeDialog} fileId={target.fileId} sampleLabel={target.sampleLabel} />
      )}
    </>
  )
}

// IGV gene browser, in a dialog.
const IgvButton = ({ analysisRow }) => {
  const { target, open, openDialog, closeDialog } = useRowDialog(analysisRow)

  return (
    <>
      <Tooltip title="IGV Gene Browser" arrow>
        <ResultButton onClick={openDialog}>IGV</ResultButton>
      </Tooltip>
      {target && (
        <IgvGeneBrowserDialog
          open={open}
          onClose={closeDialog}
          fileId={target.fileId}
          analysisWrapperId={target.analysisId}
          sampleLabel={target.sampleLabel}
        />
      )}
    </>
  )
}

// A paired RNA on its DNA row: the RNA page once it finished, otherwise its
// state, so a running RNA reads as running rather than missing.
const PairedRnaItem = ({ rna }) => {
  const name = rna.display_sample_name
  // The list sends 'done' / 'running' / 'failed'; the fallback lookup sends upper case.
  const status = String(rna.status || '').toUpperCase()
  if (status === 'DONE') {
    return (
      <GoToRnaAnalysis
        analysisWrapperId={rna.id}
        title={name ? `Paired RNA: ${name}` : 'Paired RNA'}
        testId="paired-rna"
      />
    )
  }
  const failed = status === 'FAILED' || status === 'UNKNOWN'
  return (
    <Tooltip title={name ? `Paired RNA: ${name}` : 'Paired RNA'} arrow>
      <span>
        <TonalChip
          tone={failed ? 'error' : 'info'}
          label={failed ? 'RNA failed' : 'RNA running'}
          data-testid="paired-rna-status"
        />
      </span>
    </Tooltip>
  )
}

// Result & Actions: left-aligned groups in a fixed order (result pages, sample
// tools, report, row actions). Buttons in a group sit 6px apart and groups
// 14px apart, so the spacing alone separates them. A group that renders
// nothing (the variant table link before its metadata loads, a missing report)
// collapses, and a group that no longer fits moves to the next line whole, so
// wrapped rows keep the same 6px line gap.
const ACTION_GAP = '6px'
const ACTION_GROUP_GAP = '14px'
const actionGroupSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: ACTION_GAP,
  '&:empty': { display: 'none' },
}
const resultCellWrapSx = {
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  columnGap: ACTION_GROUP_GAP,
  rowGap: ACTION_GAP,
}
// Up to six result and tool buttons (a germline sequencing row: SNV/Indel, CNV,
// MOV | QC, Coverage, IGV | Report | pipeline and hide icons, about 575px) stay
// on one line from 1440px up. The column then takes the width of the longest
// such row on the page, not a fixed reserve, so Analysis Type keeps the rest.
// Narrower screens wrap by group.
const RESULT_ONE_LINE_MAX = 6
const RESULT_ONE_LINE_WIDTH = 575
const resultCellSx = {
  ...resultCellWrapSx,
  // The groups are Boxes; the class keeps this rule above their own wrap.
  '@media (min-width: 1440px)': { flexWrap: 'nowrap', '& > .MuiBox-root': { flexWrap: 'nowrap' } },
}
// Longer rows (ONT and PacBio with SV and methylation) wrap by group within a
// one-line germline width instead of widening the column.
const resultCellLongSx = {
  ...resultCellWrapSx,
  '@media (min-width: 1440px)': { minWidth: RESULT_ONE_LINE_WIDTH },
}

// Row actions after the report: pipeline tasks (superusers only) and hide /
// restore, as quiet 30px icons in the secondary text colour. Hiding is
// org-wide and reversible: the row moves between the default list and the
// Hidden filter, it is never deleted.
const rowActionsSx = { display: 'flex', alignItems: 'center', gap: ACTION_GAP }
const RowActions = ({ analysisRow, isSuperuser, handleToggleHidden }) => {
  const hideLabel = analysisRow.is_hidden ? 'Restore to list' : 'Hide from list'

  return (
    <Box sx={rowActionsSx}>
      {isSuperuser && (
        <Tooltip title="Open pipeline tasks" arrow>
          <ResultIconButton
            component={RouterLink}
            to={`${PATH_DASHBOARD.pipelines}/${analysisRow.pipeline_id}/tasks`}
            onClick={(e) => e.stopPropagation()}
            aria-label="Open pipeline tasks"
          >
            <Icon icon={activityFill} width={18} height={18} />
          </ResultIconButton>
        </Tooltip>
      )}
      <Tooltip title={hideLabel} arrow>
        <ResultIconButton
          onClick={(e) => {
            e.stopPropagation()
            handleToggleHidden(analysisRow.analysis_id, !analysisRow.is_hidden)
          }}
          aria-label={hideLabel}
        >
          {analysisRow.is_hidden ? <VisibilityIcon /> : <VisibilityOffIcon />}
        </ResultIconButton>
      </Tooltip>
    </Box>
  )
}

// Status holds only the status chip and the done count, so the column is as
// narrow as the widest chip on the page (the status box itself is 150px wide).
const statusCellSx = { '& > div': { width: 'auto' } }
// Status and Date keep their content width; the rest goes to Analysis Type,
// Sample and Result & Actions.
const tightColumnProps = () => ({ style: { width: '1%' } })
// From 1600px up Analysis Type takes its share of the spare width before
// Sample does, so four chips, details and Run AI (about 410px) stay on one
// line. At 1440px the result row needs that room, so the table splits it evenly.
const typeColumnHeadProps = () => ({ sx: { '@media (min-width: 1600px)': { width: '28%' } } })

// A sample started as part of a batch names that batch under the sample in
// link colour (no underline), linking to the batch page.
const BatchLink = ({ analysisRow }) => {
  const batch = getRowBatch(analysisRow)
  if (!batch) return null
  return (
    <Typography
      component={RouterLink}
      to={batchPath(batch.id)}
      noWrap
      sx={{
        display: 'block',
        mt: 0.25,
        fontSize: '0.78125rem', // 12.5px
        lineHeight: 1.4,
        color: 'primary.main',
        textDecoration: 'none',
        '&:hover': { color: 'primary.dark', textDecoration: 'none' },
      }}
    >
      {typeof batch.name === 'string' && batch.name.trim() ? batch.name : 'Batch'}
    </Typography>
  )
}

const SampleAliasCell = ({ analysisRow, onRefresh, showBlurryLoading }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const committedRef = useRef(false)
  const details = useRowDialog(analysisRow)
  const showDetails = SAMPLE_ANALYSIS_TYPES.includes(analysisRow.analysis_type) && Boolean(analysisRow.file_id)

  const startEdit = (e) => {
    e.stopPropagation()
    committedRef.current = false
    // Prefill with what is on screen (alias, or the sample/cohort label).
    // An empty box would contradict "default is the sample name".
    setDraft(analysisRow.sample_alias || sampleDisplayLabel(analysisRow) || '')
    setEditing(true)
  }

  const cancel = () => {
    committedRef.current = true
    setEditing(false)
  }

  const save = async () => {
    if (committedRef.current) return
    committedRef.current = true
    const initial = analysisRow.sample_alias || sampleDisplayLabel(analysisRow) || ''
    if (draft === initial) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await updateSampleAlias(analysisRow.analysis_id, draft)
      setEditing(false)
      if (onRefresh) await onRefresh()
    } catch (err) {
      console.error('Failed to save sample alias', err)
      committedRef.current = false
    } finally {
      setSaving(false)
    }
  }

  const stopRowClick = (e) => e.stopPropagation()

  const content = editing ? (
    <Box onClick={stopRowClick} onMouseDown={stopRowClick} style={{ minWidth: 180 }}>
      <TextField
        autoFocus
        size="small"
        variant="outlined"
        fullWidth
        value={draft}
        disabled={saving}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') {
            e.preventDefault()
            save()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            cancel()
          }
        }}
        onBlur={save}
        inputProps={{ maxLength: 255, 'aria-label': 'Sample display name' }}
      />
    </Box>
  ) : (
    <Box display="flex" alignItems="center" style={{ gap: 4 }} onClick={stopRowClick} onMouseDown={stopRowClick}>
      <Tooltip title="Edit sample display name" arrow>
        <IconButton size="small" onClick={startEdit} aria-label="Edit sample display name" style={{ flexShrink: 0 }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Box sx={sampleNameSx}>
        <FileNameText name={sampleDisplayLabel(analysisRow)} variant="plain" />
        <BatchLink analysisRow={analysisRow} />
      </Box>
      {showDetails && (
        <Tooltip title="Sample details: phenotype, diseases, notes" arrow>
          <IconButton
            size="small"
            onClick={details.openDialog}
            aria-label="Sample details: phenotype, diseases, notes"
            style={{ flexShrink: 0 }}
          >
            <SampleDetailsIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  )

  // The dialog sits outside the row-click guard above so its own clicks and
  // popovers behave as anywhere else.
  return (
    <>
      {showBlurryLoading ? <Skeleton>{content}</Skeleton> : content}
      {details.target && (
        <SampleDetailsDialog
          open={details.open}
          onClose={details.closeDialog}
          fileId={details.target.fileId}
          sampleName={details.target.sampleName}
          sampleLabel={details.target.sampleLabel}
        />
      )}
    </>
  )
}

export const createAnalysisTableColumns = ({
  user,
  processedData,
  analysisData,
  handleDownloadReport,
  handleDownloadVcf,
  handleToggleHidden,
  showBlurryLoading,
  onRefresh,
  ownOrganisationId = null,
}) => [
  {
    name: 'status',
    label: 'Status',
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: tightColumnProps,
      setCellProps: tightColumnProps,
      customBodyRenderLite: (dataIndex) => {
        const analysisRow = analysisData[dataIndex]
        const status = analysisRow.status
        const content = (
          <Box sx={statusCellSx}>
            <AnalysisStatus status={status} analysisRow={analysisRow} />
          </Box>
        )
        return showBlurryLoading ? <Skeleton>{content}</Skeleton> : content
      },
    },
  },
  {
    name: 'analysis_type',
    label: 'Analysis Type',
    options: {
      filter: false,
      sort: false,
      setCellHeaderProps: typeColumnHeadProps,
      customBodyRenderLite: (dataIndex) => {
        const analysisRow = analysisData[dataIndex]
        // AI prioritisation for a finished germline variant result that ran without it.
        // AI runs are charged to and started in the user's own organisation only, so a
        // sub-organisation's row, listed to its parent, does not offer one.
        const otherOrganisation =
          ownOrganisationId &&
          analysisRow?.organisation?.id &&
          String(analysisRow.organisation.id) !== String(ownOrganisationId)
        const showRunAI = canRunAI(analysisRow) && !otherOrganisation
        const content = <AnalysisTypeChips analysisData={analysisRow} showRunAI={showRunAI} onRefresh={onRefresh} />
        return showBlurryLoading ? <Skeleton>{content}</Skeleton> : content
      },
    },
  },
  {
    name: 'sample_name',
    label: 'Sample',
    options: {
      filter: false,
      sort: false,
      customBodyRenderLite: (dataIndex) => {
        const analysisRow = analysisData[dataIndex]
        return <SampleAliasCell analysisRow={analysisRow} onRefresh={onRefresh} showBlurryLoading={showBlurryLoading} />
      },
    },
  },
  {
    name: 'created_at',
    label: 'Date',
    options: {
      filter: false,
      sort: true,
      setCellHeaderProps: tightColumnProps,
      setCellProps: tightColumnProps,
      customBodyRenderLite: (dataIndex) => {
        const analysisRow = analysisData[dataIndex]
        const content = (
          <Typography variant="body2" noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatDateTime(analysisRow?.created_at)}
          </Typography>
        )
        return showBlurryLoading ? <Skeleton>{content}</Skeleton> : content
      },
    },
  },
  {
    name: 'result',
    label: 'Result & Actions',
    options: {
      filter: false,
      sort: false,
      empty: true,
      customBodyRenderLite: (dataIndex) => {
        const analysisRow = analysisData[dataIndex]
        const status = analysisRow.status
        const finishedPipelines = analysisRow.finished_pipelines // array field
        const isDone = status === 'DONE'
        const isSequenceFile = SEQUENCE_ANALYSIS_TYPES.includes(analysisRow.analysis_type)
        const isSampleFile = SAMPLE_ANALYSIS_TYPES.includes(analysisRow.analysis_type)
        // Only built (and counted) for rows that show them, as before.
        const showsResultGroups =
          analysisRow.analysis_type !== 'GENESUS-COHORT' &&
          analysisRow.analysis_type !== 'EXOMEDEPTH-COHORT' &&
          analysisRow.analysis_type !== 'CARRIER' &&
          !(analysisRow.analysis_type === 'Multisample' && finishedPipelines.includes('MULTISAMPLE')) &&
          (status === 'DONE' || finishedPipelines.length > 0)
        // 1. Result pages. SNV/Indel button (main result, shown first). Somatic and germline are
        // separate views over separate parquets with separate column orders, so the same
        // "VCF is done" state routes to two different destinations.
        // The "Somatic" tag is the discriminator: analysis_type is 'VCF'
        // for both, and it is set at analysis creation alongside
        // VcfFile.is_somatic, which is what the pipeline itself routes on.
        const isRna = Boolean(analysisRow.tags?.includes('RNA'))
        const resultPages = showsResultGroups
          ? [
              // A tumour RNA analysis: its own page (fusions, splice variants,
              // expression, library QC); it has no variant dashboard.
              isRna && finishedPipelines.includes('RNA') && (
                <GoToRnaAnalysis key="rna" analysisWrapperId={analysisRow.analysis_id} />
              ),
              finishedPipelines.includes('VCF') &&
                (analysisRow.tags?.includes('Somatic') ? (
                  <GoToSomaticVariants key="snv" analysisWrapperId={analysisRow.analysis_id} />
                ) : (
                  <AnalysisPopover key="snv" fileId={analysisRow.file_id} sampleName={analysisRow.sample_name} />
                )),
              showSomaticStructuralButton(analysisRow) && (
                <GoToSomaticStructural key="structural" analysisWrapperId={analysisRow.analysis_id} />
              ),
              // CNV Button (FASTQ-based)
              analysisRow.analysis_type !== 'ONT_BAM' &&
                analysisRow.analysis_type !== 'PACBIO_BAM' &&
                analysisRow.analysis_parameters?.annotation?.runCnv === true &&
                (analysisRow.status === 'DONE' || finishedPipelines.includes('CNV')) && (
                  <GoToCnvDashboard key="cnv" analysisWrapperId={analysisRow.analysis_id} />
                ),
              // ONT CNV Button
              analysisRow.analysis_type === 'ONT_BAM' && finishedPipelines.includes('CNV') && (
                <GoToCnvDashboard
                  key="ont-cnv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="ont-cnv"
                  label="CNV"
                />
              ),
              // ONT SV Button
              analysisRow.analysis_type === 'ONT_BAM' && finishedPipelines.includes('SV') && (
                <GoToCnvDashboard
                  key="ont-sv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="ont-sv"
                  label="SV"
                />
              ),
              // PacBio CNV Button
              analysisRow.analysis_type === 'PACBIO_BAM' && finishedPipelines.includes('CNV') && (
                <GoToCnvDashboard
                  key="pacbio-cnv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="pacbio-cnv"
                  label="CNV"
                />
              ),
              // PacBio SV Button
              analysisRow.analysis_type === 'PACBIO_BAM' && finishedPipelines.includes('SV') && (
                <GoToCnvDashboard
                  key="pacbio-sv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="pacbio-sv"
                  label="SV"
                />
              ),
              // VCF CNV Button (uploaded CNV VCF)
              analysisRow.analysis_type === 'VCF' && finishedPipelines.includes('CNV') && (
                <GoToCnvDashboard
                  key="vcf-cnv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="vcf-cnv"
                  label="CNV"
                />
              ),
              // VCF SV Button (uploaded SV VCF)
              analysisRow.analysis_type === 'VCF' && finishedPipelines.includes('SV') && (
                <GoToCnvDashboard
                  key="vcf-sv"
                  analysisWrapperId={analysisRow.analysis_id}
                  cnvType="vcf-sv"
                  label="SV"
                />
              ),
              // Methylation Button (ONT & PacBio)
              ['ONT_BAM', 'PACBIO_BAM'].includes(analysisRow.analysis_type) && finishedPipelines.includes('MOD') && (
                <GoToMethylationDashboard key="methylation" analysisWrapperId={analysisRow.analysis_id} />
              ),
              // Multi-Omic View (PROJECT-U) Button: TRACKS, or Somatic plus
              // SOMATIC_UNIFIED (lib/unifiedButton.js).
              showUnifiedButton(analysisRow) && (
                <GoToUnifiedDashboard key="mov" analysisWrapperId={analysisRow.analysis_id} />
              ),
            ].filter(Boolean)
          : []
        // 2. Sample tools. QC dashboard and gene coverage exist for sequence files only
        // (VCF uploads have no QC); the IGV gene browser for every sample, RNA included
        // (its alignment is served under the same file id).
        const sampleTools = showsResultGroups
          ? [
              isDone && isSequenceFile && !isRna && (
                <GoToSampleDashboard key="qc" fileId={analysisRow.file_id} sampleName={analysisRow.sample_name} />
              ),
              // Gene coverage reads DNA capture depth; an RNA analysis has none to show.
              isDone && isSequenceFile && !isRna && <CoverageButton key="coverage" analysisRow={analysisRow} />,
              isDone && isSampleFile && <IgvButton key="igv" analysisRow={analysisRow} />,
            ].filter(Boolean)
          : []
        // Paired RNA, appended to the DNA row's results whatever the DNA's own state.
        const pairedRna = pairedRnaOf(analysisRow).map((rna) => <PairedRnaItem key={`rna-${rna.id}`} rna={rna} />)
        const isLongRow = resultPages.length + pairedRna.length + sampleTools.length > RESULT_ONE_LINE_MAX
        const content = (
          <Box sx={isLongRow ? resultCellLongSx : resultCellSx}>
            {/* Groups in order: result pages, sample tools (QC, coverage, IGV),
                the report, then the row action icons. Spacing alone separates them. */}
            {/* For GENESUS-COHORT and EXOMEDEPTH-COHORT analysis, show cohort dashboard button */}
            {analysisRow.analysis_type === 'GENESUS-COHORT' || analysisRow.analysis_type === 'EXOMEDEPTH-COHORT' ? (
              <Box sx={actionGroupSx}>
                {status === 'DONE' && (
                  <GoToCohortDashboard cohortId={analysisRow.analysis_id} analysisType={analysisRow.analysis_type} />
                )}
              </Box>
            ) : /* For carrier analysis, show two action buttons */
            analysisRow.analysis_type === 'CARRIER' ? (
              <Box sx={actionGroupSx}>
                {/* Left button - go to carrier report page (show only when DONE) */}
                {status === 'DONE' && (
                  <Tooltip title="Open carrier report" arrow>
                    <ResultButton
                      primary
                      component={RouterLink}
                      to={`/libra/carrier-report/${analysisRow.analysis_id}`}
                    >
                      Carrier report
                    </ResultButton>
                  </Tooltip>
                )}

                {/* Right button - opens carrier analysis popover, using same style as AnalysisPopover */}
                <CarrierAnalysisPopover analysisRow={analysisRow} />
              </Box>
            ) : /* For multisample analysis, show analysis button without sampleName */
            analysisRow.analysis_type === 'Multisample' && finishedPipelines.includes('MULTISAMPLE') ? (
              <Box sx={actionGroupSx}>
                <AnalysisPopover fileId={analysisRow.file_id} />
              </Box>
            ) : (
              // Show action buttons only if analysis is DONE
              (status === 'DONE' || finishedPipelines.length > 0) && (
                <>
                  <Box sx={actionGroupSx}>
                    {resultPages}
                    {pairedRna}
                  </Box>
                  <Box sx={actionGroupSx}>{sampleTools}</Box>

                  {/* 3. Report */}
                  <Box sx={actionGroupSx}>
                    <ReportAvailabilityIcon
                      fileId={analysisRow.file_id}
                      analysisType={analysisRow.analysis_type}
                      isDone={status === 'DONE'}
                      sampleName={analysisRow.sample_name}
                    />
                  </Box>
                </>
              )
            )}
            {/* A DNA row without results of its own yet still opens its paired RNA. */}
            {!(status === 'DONE' || finishedPipelines.length > 0) && pairedRna.length > 0 && (
              <Box sx={actionGroupSx}>{pairedRna}</Box>
            )}

            {/* 4. Row actions: pipeline tasks (superusers) and hide / restore, on every row. */}
            <RowActions
              analysisRow={analysisRow}
              isSuperuser={Boolean(user.is_superuser)}
              handleToggleHidden={handleToggleHidden}
            />
          </Box>
        )
        return showBlurryLoading ? <Skeleton>{content}</Skeleton> : content
      },
    },
  },
]
